
import React, { useState } from 'react';
import { UserRole, User, Voter } from '../types';
import { Mail, Lock, ArrowRight, Info, Landmark } from 'lucide-react';

interface LoginProps {
  onLogin: (user: User) => void;
}

const DEFAULT_CREDENTIALS: Record<string, User> = {
  'admin@smsa-syria.org': { id: 'admin', name: 'System Admin', email: 'admin@smsa-syria.org', role: UserRole.SYSTEM_ADMIN }
};

const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const customLogo = localStorage.getItem('smsa_custom_logo');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const cleanEmail = email.toLowerCase().trim();
    
    let user = DEFAULT_CREDENTIALS[cleanEmail];
    
    if (!user) {
      const savedDelegates = localStorage.getItem('smsa_delegates');
      if (savedDelegates) {
        const delegates: Voter[] = JSON.parse(savedDelegates);
        const registeredVoter = delegates.find(v => v.email.toLowerCase() === cleanEmail);
        if (registeredVoter) {
          user = {
            id: registeredVoter.id,
            name: registeredVoter.name,
            email: registeredVoter.email,
            role: registeredVoter.role
          };
        }
      }
    }
    
    const storedPass = localStorage.getItem(`smsa_pass_${cleanEmail}`);
    const validPass = storedPass || 'password';

    if (user && password === validPass) {
      const savedProfileName = localStorage.getItem(`smsa_profile_${cleanEmail}`);
      if (savedProfileName) {
        user = { ...user, name: savedProfileName };
      }
      onLogin(user);
    } else {
      setError('Invalid identity credentials. Try password: "password"');
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-10 flex flex-col items-center">
          <div className="inline-flex items-center justify-center p-6 bg-white rounded-3xl shadow-xl border border-slate-100 mb-6 w-32 h-32 overflow-hidden">
            {customLogo ? (
              <img src={customLogo} alt="SMSA Logo" className="w-full h-full object-contain" />
            ) : (
              <Landmark className="text-indigo-600 w-20 h-20" />
            )}
          </div>
          <h1 className="text-3xl font-black text-indigo-600 tracking-tight uppercase">SMSA-Votefix</h1>
          <p className="text-slate-500 font-bold uppercase text-[10px] tracking-[0.2em] mt-1">Syrian Medical Students' Association</p>
        </div>

        <div className="bg-white rounded-[2.5rem] shadow-2xl border border-white p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="text-xs font-black text-slate-400 uppercase tracking-widest mb-2 block">Organization Email</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <input 
                  type="email" 
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-100 rounded-xl focus:ring-2 focus:ring-indigo-600/20 focus:border-indigo-600 outline-none transition-all font-medium text-slate-800"
                  placeholder="user@smsa-syria.org"
                  required
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-black text-slate-400 uppercase tracking-widest mb-2 block">Authorized Secret</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <input 
                  type="password" 
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-100 rounded-xl focus:ring-2 focus:ring-indigo-600/20 focus:border-indigo-600 outline-none transition-all font-medium"
                  placeholder="••••••••"
                  required
                />
              </div>
            </div>

            {error && <p className="text-rose-500 text-sm font-bold text-center">{error}</p>}

            <button type="submit" className="w-full py-4 bg-indigo-600 text-white rounded-2xl font-black uppercase tracking-widest hover:bg-indigo-700 transition-all shadow-xl shadow-indigo-100 flex items-center justify-center gap-2 group">
              Access Portal
              <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </form>

          <div className="mt-8 pt-6 border-t border-slate-50">
            <div className="flex items-start gap-3 bg-indigo-50 p-4 rounded-2xl text-indigo-700 border border-indigo-100">
              <Info size={20} className="shrink-0 mt-0.5" />
              <div className="text-xs leading-relaxed">
                <p className="font-bold mb-1 underline text-[10px] uppercase">Portal Bootstrap Identity:</p>
                <p className="opacity-80">Admin: <strong>admin@smsa-syria.org</strong></p>
                <p className="opacity-80">Secret: <strong>password</strong></p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="mt-8 text-center flex items-center justify-center gap-2 text-slate-400 font-bold uppercase text-[9px] tracking-widest">
           <div className="w-1.5 h-1.5 bg-indigo-600 rounded-full animate-pulse"></div>
           System Environment: SMSA-SY-NODE-1
        </div>
      </div>
    </div>
  );
};

export default Login;
